export type BuilderPanelId = 'layers' | 'theme' | 'assets';

export type BuilderPreferences = {
  zoom: number;
  outlines: boolean;
  activePanel: BuilderPanelId;
  device: { w: number; h: number };
};

export const DEFAULT_BUILDER_PREFERENCES: BuilderPreferences = {
  zoom: 1,
  outlines: false,
  activePanel: 'layers',
  device: { w: 390, h: 844 },
};
export const BUILDER_PREFERENCES_KEY = 'phq.builder.preferences.v1';

export function browserBuilderPreferenceStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

export function readBuilderPreferences(storage: Pick<Storage, 'getItem'> | undefined): BuilderPreferences {
  if (!storage) return DEFAULT_BUILDER_PREFERENCES;
  try {
    const parsed: unknown = JSON.parse(storage.getItem(BUILDER_PREFERENCES_KEY) ?? 'null');
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_BUILDER_PREFERENCES;
    const value = parsed as Record<string, unknown>;
    return {
      zoom: validZoom(value.zoom) ? value.zoom : DEFAULT_BUILDER_PREFERENCES.zoom,
      outlines: typeof value.outlines === 'boolean' ? value.outlines : DEFAULT_BUILDER_PREFERENCES.outlines,
      activePanel: validPanel(value.activePanel) ? value.activePanel : DEFAULT_BUILDER_PREFERENCES.activePanel,
      device: validDevice(value.device) ? value.device : DEFAULT_BUILDER_PREFERENCES.device,
    };
  } catch {
    return DEFAULT_BUILDER_PREFERENCES;
  }
}

export function writeBuilderPreferences(
  storage: Pick<Storage, 'setItem'> | undefined,
  preferences: BuilderPreferences
): void {
  if (!storage) return;
  try {
    storage.setItem(BUILDER_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch {
    // ponytail: preferences are ergonomic only; storage denial must never stop authoring.
  }
}

function validZoom(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0.25 && value <= 2;
}

function validPanel(value: unknown): value is BuilderPanelId {
  return value === 'layers' || value === 'theme' || value === 'assets';
}

function validDevice(value: unknown): value is { w: number; h: number } {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return validDimension(v.w) && validDimension(v.h);
}

// Same 200–2048 clamp the toolbar's custom Width/Height fields enforce.
function validDimension(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 200 && value <= 2048;
}
